import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router";
import { classrooms as classroomQuerys } from "@nualang/nualang-api-and-queries/Queries";
import { styled } from "@mui/material/styles";
import {
  invalidateCache,
  DataType,
} from "@nualang/nualang-api-and-queries/APIs/invalidation";
import {
  getClassrooms,
} from "@nualang/nualang-api-and-queries/APIs/Classrooms";
import { getCourses } from "@nualang/nualang-api-and-queries/APIs/Courses";
// import SelectAssignmentTypeDialog from "@nualang/nualang-ui-components/dist/Assignments/SelectAssignmentTypeDialog";
import CreateAssignmentDialog from "@nualang/nualang-ui-components/dist/Assignments/CreateAssignmentDialog";
import { classrooms as classroomHelpers, users as usersHelpers } from "../../../utils/helpers";
import { getCourseSections } from "@nualang/nualang-api-and-queries/APIs/CourseSections";
import { getCourseSectionTopicRoleplays } from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopicRoleplays";
import { getCourseSectionTopicBots } from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopicBots";
import config from "../../../config";
import { getClassroomMembers } from "@nualang/nualang-api-and-queries/APIs/ClassroomMembers";
import {
  createAssignment as createAssignmentAPI,
  createAssignees as createAssigneesAPI,
} from "@nualang/nualang-api-and-queries/APIs/Assignments";
import { assignments as assignmentQuerys } from "@nualang/nualang-api-and-queries/Queries";
import { useApp } from "../../../hooks";

const Root = styled("div")(({ theme }) => ({
  padding: theme.spacing(1, 2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(2, 3),
  },
}));

function CreateAssignment(props) {
  const {
    authData: {
      username,
      attributes: { email },
    },
  } = props;
  const params = useParams();
  const navigate = useNavigate();
  const { classroomId } = params;
  const { t } = useTranslation();
  const [isAssignExercisesOpen, setIsAssignExercisesOpen] = useState(true);
  const { openSnackbar, startLoading, stopLoading } = useApp();

  const getMemberDetails = (membersList) => usersHelpers.fetchMembersDetails(membersList);

  const handleClose = () => {
    if (window.history?.length && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/", { replace: true });
    }
  };

  const teachClassroomsQuery = classroomQuerys.useClassrooms(
    async (filters) => {
      const classrooms = await getClassrooms(filters);
      const formattedClassrooms = await classroomHelpers.formatClassrooms(
        classrooms.Items,
        false,
        props.authData,
      );
      return formattedClassrooms;
    },
    {
      filters: {
        createdBy: username,
        verbose: false,
        // Get all using high limit
        limit: config.limits.teachClassrooms,
      },
    },
    { enabled: !!username },
  );

  const classrooms =
    teachClassroomsQuery.isSuccess &&
    Array.isArray(teachClassroomsQuery.data) &&
    teachClassroomsQuery.data.length
      ? teachClassroomsQuery.data.filter((c) => c.visibility !== "archived")
      : [];
  const createAssignmentMutation = assignmentQuerys.useCreateAssignment(
    createAssignmentAPI,
    {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: async (_, variables) => {
        openSnackbar(t("assignment_created"), "success");
        if (variables?.classroomId) {
          await invalidateCache(DataType.CLASSROOM, {
            classroomId: variables?.classroomId,
          });
        }
      },
      onError: () => {
        openSnackbar("problem_creating", "error");
      },
    },
  );

  const createAssigneesMutation = assignmentQuerys.useCreateAssignees(
    createAssigneesAPI,
    {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: () => {
        openSnackbar(t("assignees_created"), "success");
      },
      onError: () => {
        openSnackbar("problem_creating", "error");
      },
    },
  );

  const handleCreateAssignment = async (assignment) => {
    startLoading();
    try {
      const response = await createAssignmentMutation.mutateAsync({
        classroomId: assignment.classroomId,
        body: assignment,
      });
      const assigneeResponse = await createAssigneesMutation.mutateAsync({
        classroomId: assignment.classroomId,
        assignmentId: response.Item.assignmentId,
        body: {
          assignees: assignment.assignedStudents,
        },
      });
      return {
        ...response.Item,
        ...assigneeResponse.Item,
      };
    } catch (error) {
      // Handle error if mutation fails
      console.error("Error creating assignment:", error);
    } finally {
      stopLoading();
    }
  };

  return (
    <Root>
      {/* <SelectAssignmentTypeDialog
        t={t}
        open={true}
        handleClose={handleClose}
        dialogTitle={t("create_assignment")}
        classrooms={[]}
        handleSelectExercises={() => setIsAssignExercisesOpen(true)}
      /> */}
      <CreateAssignmentDialog
        t={t}
        userEmail={email}
        dialogTitle={"create_assignment"}
        open={isAssignExercisesOpen}
        classrooms={classrooms}
        classroom={classroomId}
        getCourseSections={getCourseSections}
        getRoleplays={getCourseSectionTopicRoleplays}
        getBots={getCourseSectionTopicBots}
        username={username}
        createAssignment={handleCreateAssignment}
        handleClose={() => {
          setIsAssignExercisesOpen(false);
          handleClose();
        }}
        getClassroomMembers={getClassroomMembers}
        getMemberDetails={getMemberDetails}
        getCourses={getCourses}
      />
    </Root>
  );
}

CreateAssignment.propTypes = {
  authData: PropTypes.object,
  navigate: PropTypes.func,
};

export default CreateAssignment;
